"use client"

import { useState, useEffect } from "react"
import { db } from "../firebase"
import { collection, addDoc, deleteDoc, getDocs, doc, updateDoc } from "firebase/firestore"
import { FaCalendarPlus, FaTrash, FaEdit, FaCalendarAlt, FaEye, FaPlus, FaTimes, FaSave } from "react-icons/fa"

const EventsUpdate = () => {
  const [form, setForm] = useState({
    Heading: "",
    paragraph: "",
    image: "",
    fullDescription: "",
    dateFrom: "",
    dateTo: "",
  })

  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [previewImage, setPreviewImage] = useState("")
  const [editingEvent, setEditingEvent] = useState(null)
  const [isEditMode, setIsEditMode] = useState(false)

  const fetchEvents = async () => {
    setLoading(true)
    try {
      const snapshot = await getDocs(collection(db, "events"))
      setEvents(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })))
    } catch (error) {
      console.error("Error fetching events:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const { Heading, paragraph, image, fullDescription, dateFrom, dateTo } = form

    if (Heading && paragraph && image && fullDescription && dateFrom && dateTo) {
      setLoading(true)
      try {
        if (isEditMode && editingEvent) {
          // Update existing event
          await updateDoc(doc(db, "events", editingEvent.id), {
            ...form,
            updatedAt: new Date().toISOString(),
          })
        } else {
          // Add new event
          await addDoc(collection(db, "events"), {
            ...form,
            createdAt: new Date().toISOString(),
          })
        }

        setForm({
          Heading: "",
          paragraph: "",
          image: "",
          fullDescription: "",
          dateFrom: "",
          dateTo: "",
        })
        setPreviewImage("")
        setShowForm(false)
        setIsEditMode(false)
        setEditingEvent(null)

        setTimeout(() => {
          fetchEvents()
        }, 200)
      } catch (error) {
        console.error("Error saving event:", error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleEdit = (event) => {
    setForm({
      Heading: event.Heading,
      paragraph: event.paragraph,
      image: event.image,
      fullDescription: event.fullDescription,
      dateFrom: event.dateFrom,
      dateTo: event.dateTo,
    })
    setPreviewImage(event.image)
    setEditingEvent(event)
    setIsEditMode(true)
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this event?")) {
      setLoading(true)
      try {
        await deleteDoc(doc(db, "events", id))
        fetchEvents()
      } catch (error) {
        console.error("Error deleting event:", error)
      } finally {
        setLoading(false)
      }
    }
  }

  const handleImageChange = (e) => {
    const imageUrl = e.target.value
    setForm({ ...form, image: imageUrl })
    setPreviewImage(imageUrl)
  }

  const handleCancelEdit = () => {
    setForm({
      Heading: "",
      paragraph: "",
      image: "",
      fullDescription: "",
      dateFrom: "",
      dateTo: "",
    })
    setPreviewImage("")
    setShowForm(false)
    setIsEditMode(false)
    setEditingEvent(null)
  }

  useEffect(() => {
    fetchEvents()
  }, [])

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-6 mb-8 border border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center space-x-4">
              <div className="p-4 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl shadow-lg">
                <FaCalendarAlt className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white bg-gradient-to-r from-gray-900 to-gray-700 dark:from-white dark:to-gray-300 bg-clip-text text-transparent">
                  Events Management
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2 text-lg">
                  Create, manage and organize your events seamlessly
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                if (isEditMode) {
                  handleCancelEdit()
                } else {
                  setShowForm(!showForm)
                }
              }}
              className={`${
                isEditMode
                  ? "bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700"
                  : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
              } text-white px-6 py-3 rounded-xl flex items-center space-x-3 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 font-medium`}
            >
              {isEditMode ? (
                <FaTimes className="h-5 w-5" />
              ) : showForm ? (
                <FaTimes className="h-5 w-5" />
              ) : (
                <FaPlus className="h-5 w-5" />
              )}
              <span>{isEditMode ? "Cancel Edit" : showForm ? "Cancel" : "Add Event"}</span>
            </button>
          </div>
        </div>

        {/* Add/Edit Event Form */}
        {showForm && (
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 mb-8 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center space-x-3 mb-8">
              <div
                className={`p-3 rounded-xl ${
                  isEditMode
                    ? "bg-gradient-to-r from-blue-500 to-indigo-600"
                    : "bg-gradient-to-r from-green-500 to-emerald-600"
                } shadow-lg`}
              >
                {isEditMode ? (
                  <FaEdit className="h-6 w-6 text-white" />
                ) : (
                  <FaCalendarPlus className="h-6 w-6 text-white" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {isEditMode ? "Edit Event" : "Create New Event"}
                </h2>
                {isEditMode && (
                  <p className="text-gray-600 dark:text-gray-400 mt-1">Editing: {editingEvent?.Heading}</p>
                )}
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Event Title
                    </label>
                    <input
                      type="text"
                      value={form.Heading}
                      onChange={(e) => setForm({ ...form, Heading: e.target.value })}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200 text-lg"
                      placeholder="Enter event title..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Short Description
                    </label>
                    <input
                      type="text"
                      value={form.paragraph}
                      onChange={(e) => setForm({ ...form, paragraph: e.target.value })}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200"
                      placeholder="Brief description of the event..."
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={form.dateFrom}
                        onChange={(e) => setForm({ ...form, dateFrom: e.target.value })}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={form.dateTo}
                        onChange={(e) => setForm({ ...form, dateTo: e.target.value })}
                        className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200"
                        required
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                      Event Image URL
                    </label>
                    <input
                      type="url"
                      value={form.image}
                      onChange={handleImageChange}
                      className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200"
                      placeholder="https://example.com/image.jpg"
                      required
                    />
                    {previewImage && (
                      <div className="mt-4 relative">
                        <img
                          src={previewImage || "/placeholder.svg"}
                          alt="Preview"
                          className="w-full h-48 rounded-xl object-cover border-2 border-gray-200 dark:border-gray-600 shadow-lg"
                          onError={(e) => (e.target.src = "/placeholder.svg?height=192&width=400")}
                        />
                        <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded-lg text-xs font-medium">
                          Preview
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                  Full Description
                </label>
                <textarea
                  rows={6}
                  value={form.fullDescription}
                  onChange={(e) => setForm({ ...form, fullDescription: e.target.value })}
                  className="w-full p-4 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-green-500 dark:focus:border-green-400 focus:ring-4 focus:ring-green-100 dark:focus:ring-green-900 transition-all duration-200 resize-none"
                  placeholder="Detailed description of the event, including agenda, speakers, venue details, etc..."
                  required
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                {isEditMode && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="px-8 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-medium"
                  >
                    Cancel Edit
                  </button>
                )}
                <button
                  type="submit"
                  disabled={loading}
                  className={`px-8 py-3 rounded-xl text-white font-medium flex items-center space-x-3 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed ${
                    isEditMode
                      ? "bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                      : "bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                  }`}
                >
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : isEditMode ? (
                    <FaSave className="h-5 w-5" />
                  ) : (
                    <FaPlus className="h-5 w-5" />
                  )}
                  <span>
                    {loading
                      ? isEditMode
                        ? "Updating..."
                        : "Adding..."
                      : isEditMode
                        ? "Update Event"
                        : "Create Event"}
                  </span>
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Events Display */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-600 rounded-xl shadow-lg">
                <FaEye className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Current Events</h2>
                <p className="text-gray-600 dark:text-gray-400">
                  {events.length} event{events.length !== 1 ? "s" : ""} total
                </p>
              </div>
            </div>
          </div>

          {loading && events.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-16">
              <div className="p-6 bg-gray-100 dark:bg-gray-700 rounded-2xl inline-block mb-4">
                <FaCalendarAlt className="h-16 w-16 text-gray-400 mx-auto" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No events yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">Create your first event to get started</p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white px-6 py-3 rounded-xl flex items-center space-x-2 mx-auto transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <FaPlus className="h-5 w-5" />
                <span>Create Event</span>
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
              {events.map((event) => (
                <div
                  key={event.id}
                  className={`bg-white dark:bg-gray-700 border-2 rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 ${
                    editingEvent?.id === event.id
                      ? "border-blue-500 ring-4 ring-blue-100 dark:ring-blue-900"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500"
                  }`}
                >
                  <div className="relative">
                    <img
                      src={event.image || "/placeholder.svg"}
                      alt={event.Heading}
                      className="w-full h-56 object-cover"
                      onError={(e) => (e.target.src = "/placeholder.svg?height=224&width=400")}
                    />
                    {editingEvent?.id === event.id && (
                      <div className="absolute top-3 right-3 bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                        <FaEdit className="h-3 w-3" />
                        <span>Editing</span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold mb-3 text-gray-900 dark:text-white line-clamp-2">
                      {event.Heading}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 mb-4 line-clamp-2 leading-relaxed">
                      {event.paragraph}
                    </p>
                    <div className="flex items-center text-sm text-gray-500 dark:text-gray-400 mb-6 bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                      <FaCalendarAlt className="h-4 w-4 mr-2 text-green-500" />
                      <span className="font-medium">
                        {event.dateFrom} to {event.dateTo}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <button
                        onClick={() => handleEdit(event)}
                        className="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <FaEdit className="h-4 w-4" />
                        <span className="font-medium">Edit</span>
                      </button>
                      <button
                        onClick={() => handleDelete(event.id)}
                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        <FaTrash className="h-4 w-4" />
                        <span className="font-medium">Delete</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default EventsUpdate
